/**
 * Signup page — `/signup`.
 *
 * Spec §5: only operators whose email domain is in `SIGNUP_ALLOWED_DOMAINS`
 * can sign up, and only with the operator-shared `SIGNUP_CODE`. The form
 * collects all three inputs, runs them through the shared `signupSchema`,
 * and posts to `/api/auth/signup`. The API enforces the gates server-side;
 * client-side validation only checks shape.
 *
 * Issue #276 — multi-tenancy Phase 5 (invitation flow):
 *   When the URL carries `?invite=<token>`, the page fetches the public
 *   preview (`GET /api/public/invites/:token`) to learn the orgName +
 *   invited email. On success:
 *     - render a banner "You've been invited to join {orgName}",
 *     - pre-fill the email field locked (read-only),
 *     - thread `inviteToken` through the signup payload so the API can
 *       atomically join the new user to the inviting org.
 *   On 404 the page surfaces a clear error with a link back to the
 *   standard signup. The token is propagated regardless of preview
 *   success — the signup API is the authoritative gate (a race between
 *   preview and signup is possible if an OWNER revokes mid-flow).
 */

import { useEffect } from "react";
import type { ReactElement } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Link as MuiLink,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import type { SignupInput } from "@ottopilot/shared";
import { signupSchema } from "@ottopilot/shared";
import { useMe, useSignup } from "../hooks/useAuth";
import { useInvitePreview } from "../hooks/useOrgs";
import { useToast } from "../components/ToastProvider";
import { makeZodResolver } from "../lib/zodResolver";

/**
 * Extract the optional `invite` search param. Returns `null` when
 * the URL has no token (the standard signup path).
 */
function useInviteTokenFromSearch(): string | null {
  // The signup route declares `?invite=` via `validateSearch` in
  // `router.tsx`, so this returns the narrowed `{ invite?: string }`
  // shape.  We still trim defensively in case a fresh string lands here
  // before the router-side trim (e.g. a programmatic `navigate` call).
  const search = useSearch({ from: "/signup" });
  const raw = search.invite;
  if (typeof raw === "string" && raw.trim().length > 0) return raw.trim();
  return null;
}

export function SignupPage(): ReactElement {
  const navigate = useNavigate();
  const me = useMe();
  const signup = useSignup();
  const toast = useToast();
  const inviteToken = useInviteTokenFromSearch();
  const invitePreview = useInvitePreview(inviteToken);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<SignupInput>({
    resolver: makeZodResolver(signupSchema),
    defaultValues: { email: "", password: "", signupCode: "" },
  });

  useEffect(() => {
    if (me.data?.user) {
      void navigate({ to: "/sites" });
    }
  }, [me.data, navigate]);

  // When the invite preview lands, lock the email field to the invited
  // address + thread the token through the form payload. We use
  // `setValue` so the form state matches what the user sees, ensuring
  // the Zod resolver doesn't fight the locked input.
  useEffect(() => {
    if (inviteToken) {
      setValue("inviteToken", inviteToken);
    }
    if (invitePreview.data?.email) {
      setValue("email", invitePreview.data.email);
    }
  }, [inviteToken, invitePreview.data, setValue]);

  const onSubmit = handleSubmit((values) => {
    signup.mutate(values, {
      onSuccess: () => {
        toast.showSuccess(
          invitePreview.data
            ? `Account created — you joined ${invitePreview.data.orgName}.`
            : "Account created.",
        );
        void navigate({ to: "/sites" });
      },
      onError: (err) => {
        toast.showError(err.message || "Signup failed.");
      },
    });
  });

  const previewLoading = inviteToken !== null && invitePreview.isPending;
  const previewFailed = inviteToken !== null && invitePreview.isError;

  return (
    <Container maxWidth="sm" sx={{ py: { xs: 4, md: 8 } }}>
      <Paper sx={{ p: { xs: 3, md: 4 }, border: 1, borderColor: "divider" }}>
        <Stack spacing={3}>
          <Box>
            <Typography variant="h1" sx={{ fontSize: { xs: "2rem", md: "2.5rem" } }}>
              Create account
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Sign up requires the operator allowlist email plus the signup code.
            </Typography>
          </Box>

          {previewLoading ? (
            <Box
              sx={{ display: "flex", justifyContent: "center", py: 2 }}
              data-testid="signup-invite-loading"
            >
              <CircularProgress size={24} />
            </Box>
          ) : null}

          {previewFailed ? (
            <Alert severity="warning" data-testid="signup-invite-error">
              This invite link is no longer valid. It may have expired or already been used.{" "}
              <MuiLink component={Link} to="/signup">
                Continue to standard signup
              </MuiLink>
              .
            </Alert>
          ) : null}

          {invitePreview.data ? (
            <Alert severity="info" data-testid="signup-invite-banner">
              You&apos;ve been invited to join <strong>{invitePreview.data.orgName}</strong>.
            </Alert>
          ) : null}

          {signup.isError ? (
            <Alert severity="error" data-testid="signup-error-alert">
              {signup.error.message}
            </Alert>
          ) : null}

          <Box
            component="form"
            onSubmit={(e) => {
              void onSubmit(e);
            }}
            noValidate
          >
            <Stack spacing={2}>
              <TextField
                label="Email"
                type="email"
                autoComplete="email"
                fullWidth
                error={Boolean(errors.email)}
                helperText={
                  errors.email?.message ??
                  (invitePreview.data ? "Pre-filled from your invitation." : undefined)
                }
                inputProps={{
                  "data-testid": "signup-email-input",
                  readOnly: invitePreview.data !== undefined,
                }}
                {...register("email")}
              />
              <TextField
                label="Password"
                type="password"
                autoComplete="new-password"
                fullWidth
                error={Boolean(errors.password)}
                helperText={errors.password?.message ?? "At least 8 characters."}
                inputProps={{ "data-testid": "signup-password-input" }}
                {...register("password")}
              />
              <TextField
                label="Signup code"
                fullWidth
                error={Boolean(errors.signupCode)}
                helperText={errors.signupCode?.message}
                inputProps={{ "data-testid": "signup-code-input" }}
                {...register("signupCode")}
              />
              {/* Token rides as a hidden form field so a refresh / paste-restore
                  preserves it. Only register when an invite is actually
                  present: an unconditional hidden input has DOM value "",
                  which RHF reads at submit and Zod's
                  `.string().min(1).optional()` rejects (optional catches
                  undefined, not ""), silently blocking the standard
                  signup flow. */}
              {inviteToken !== null ? <input type="hidden" {...register("inviteToken")} /> : null}
              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={isSubmitting || signup.isPending}
                data-testid="signup-submit-button"
              >
                {signup.isPending ? "Creating..." : "Create account"}
              </Button>
            </Stack>
          </Box>

          <Typography variant="body2" color="text.secondary">
            Already have an account?{" "}
            <MuiLink component={Link} to="/login" data-testid="signup-login-link">
              Sign in
            </MuiLink>
          </Typography>
        </Stack>
      </Paper>
    </Container>
  );
}
